import { useState, type FormEvent } from 'react';
import { CheckCircle2, Eye, EyeOff, KeyRound, LogOut, ShieldCheck } from 'lucide-react';
import { post } from '../api';
import { useApp } from '../AppContext';
import { BrandLogo, Button, ErrorState } from '../components';

export function PasswordSetupPage() {
  const { bootstrap, refresh, logout } = useApp();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmation, setConfirmation] = useState('');
  const [showPasswords, setShowPasswords] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    if (newPassword !== confirmation) {
      setError('The new password and confirmation do not match.');
      return;
    }
    setBusy(true);
    try {
      await post('/api/auth/change-password', { currentPassword, newPassword });
      await refresh();
    } catch (requestError) {
      setError((requestError as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return <main className="login-page password-setup-page">
    <section className="login-story password-setup-story">
      <div className="login-story__brand"><BrandLogo surface="dark" /></div>
      <div className="login-story__content">
        <span className="login-kicker"><ShieldCheck size={15} /> Protect this company workspace</span>
        <h1>Your own password.<br />Before anything else.</h1>
        <p>The temporary password was created by an administrator. Replace it now so only you can enter this account.</p>
        <div className="login-benefits">
          <div><span><KeyRound size={20} /></span><div><strong>Temporary access ends</strong><small>The password you received cannot be used again.</small></div></div>
          <div><span><CheckCircle2 size={20} /></span><div><strong>Other sessions close</strong><small>Every older login for this account is invalidated.</small></div></div>
        </div>
      </div>
      <div className="login-story__footer"><span>Nosivo</span><span>{bootstrap?.workspace?.name || 'Company workspace'}</span></div>
    </section>
    <section className="login-form-panel">
      <div className="login-card password-setup-card">
        <header><span className="eyebrow">First sign-in security</span><h2>Create your password</h2><p>Signed in as <strong>{bootstrap?.user.email}</strong></p></header>
        {error && <ErrorState message={error} />}
        <form onSubmit={submit} className="login-form">
          <label><span>Temporary password</span><input type={showPasswords ? 'text' : 'password'} autoComplete="current-password" value={currentPassword} onChange={(event) => setCurrentPassword(event.target.value)} required autoFocus /></label>
          <label><span>New password</span><div className="password-input"><input type={showPasswords ? 'text' : 'password'} autoComplete="new-password" value={newPassword} onChange={(event) => setNewPassword(event.target.value)} minLength={12} required /><button type="button" onClick={() => setShowPasswords((value) => !value)} aria-label={showPasswords ? 'Hide passwords' : 'Show passwords'}>{showPasswords ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></label>
          <label><span>Confirm new password</span><input type={showPasswords ? 'text' : 'password'} autoComplete="new-password" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} minLength={12} required /></label>
          <p className="password-requirements">Use at least 12 characters with uppercase, lowercase, a number and a symbol.</p>
          <Button type="submit" busy={busy} icon={ShieldCheck}>Secure my account</Button>
        </form>
        <button className="password-setup-signout" type="button" onClick={() => void logout()}><LogOut size={16} /> Use a different account</button>
      </div>
    </section>
  </main>;
}
